import React from "react";

import dropgoTranslations from "../locales/dropgo";
import maidTranslations from "../locales/maid";

/**
 * Internationalization (i18n) utility for supporting multiple languages
 * Supports Thai (th), English (en) and Burmese (my) - the three languages
 * the shop's staff read. A key a language is missing falls back to Thai.
 */

// Language detection and storage keys
const LANGUAGE_KEY = "app_language";
const SUPPORTED_LANGUAGES = ["th", "en", "my"];
const DEFAULT_LANGUAGE = "th";

/**
 * Get current language from localStorage or default
 * @returns {string} Current language code
 */
export const getCurrentLanguage = () => {
  const stored = localStorage.getItem(LANGUAGE_KEY);
  return SUPPORTED_LANGUAGES.includes(stored) ? stored : DEFAULT_LANGUAGE;
};

/**
 * Set current language and save to localStorage
 * @param {string} lang - Language code to set
 */
export const setCurrentLanguage = (lang) => {
  if (SUPPORTED_LANGUAGES.includes(lang)) {
    localStorage.setItem(LANGUAGE_KEY, lang);
    // Trigger language change event for components to re-render
    window.dispatchEvent(new CustomEvent("languageChanged", { detail: lang }));
  }
};

/**
 * Get available languages
 * @returns {Array} Array of supported language codes
 */
export const getSupportedLanguages = () => SUPPORTED_LANGUAGES;

/**
 * Translation function
 * @param {string} key - Translation key
 * @param {Object} params - Parameters for string interpolation
 * @returns {string} Translated string
 */
export const t = (key, params = {}) => {
  const lang = getCurrentLanguage();
  const translation =
    translations[lang]?.[key] || translations[DEFAULT_LANGUAGE]?.[key] || key;

  // Simple string interpolation
  return Object.keys(params).reduce((str, param) => {
    return str.replace(new RegExp(`{${param}}`, "g"), params[param]);
  }, translation);
};

/**
 * Translation strings object. Exported so a test can hold the languages to
 * the same keys - a missing one is invisible at runtime, since it quietly
 * falls back to Thai.
 */
export const translations = {
  th: {
    // Common/General
    "common.points": "คะแนน",
    "common.baht": "บาท",
    "common.next": "ถัดไป",
    "common.back": "กลับ",
    "common.save": "บันทึก",
    "common.cancel": "ยกเลิก",
    "common.confirm": "ตกลง",
    "common.edit": "แก้ไข",
    "common.send": "ส่ง",
    "common.loading": "กำลังโหลด...",
    "common.error": "เกิดข้อผิดพลาด",
    "common.success": "สำเร็จ",
    "common.retry": "ลองอีกครั้ง",
    "common.error_sorry": "ขออภัย, เกิดข้อผิดพลาด",
    "common.close": "ปิด",
    "common.select": "เลือก",
    "common.total": "รวม",
    "common.quantity": "จำนวน",
    "common.piece": "ชิ้น",
    "common.all": "ทั้งหมด",

    // Navigation/Menu
    "nav.home": "หน้าหลัก",
    "nav.history": "ประวัติ",
    "nav.account": "บัญชี",
    "nav.benefits": "สิทธิพิเศษ",
    "nav.wallet": "กระเป๋าเงิน",
    "nav.coupon": "คูปอง",
    "coupon.available": "คูปองที่ใช้ได้",
    "coupon.save": "เก็บคูปอง",
    "coupon.used": "คูปองที่ใช้แล้ว",
    "coupon.expired": "คูปองหมดอายุ",
    "nav.notification": "แจ้งเตือน",
    "nav.branch": "สาขา",
    "nav.logout": "ออกจากระบบ",
    "nav.me": "ฉัน",

    // Menu
    "menu.about": "เกี่ยวกับ LAUNDRY STATION",
    "menu.terms_and_policies": "ข้อกำหนดเงื่อนไขและนโยบายอื่นๆ",
    "menu.delete_account": "ลบบัญชีผู้ใช้งาน",

    // Home page
    "home.remaining_balance": "ยอดเงินคงเหลือ",
    "home.top_up": "เติมเงิน",
    "home.wash": "ซัก",
    "home.dry": "อบ",
    "home.wash_dry": "ซัก-อบ",
    "home.fold": "พับ",
    "home.services": "บริการ",
    "home.drop_and_go": "DROP & GO",
    "home.check_nearby_branch": "เช็คสาขาใกล้ฉัน",
    "home.my_basket": "ตระกร้าของฉัน",
    "home.maid": "แม่บ้าน",

    // Machine types
    "machine.washer": "เครื่องซัก",
    "machine.dryer": "เครื่องอบ",
    "machine.washer_dryer": "เครื่องซัก-อบ",
    "machine.wash_service": "ซัก {size} kg.",
    "machine.dry_service": "อบ {size} kg.",
    "machine.wash_dry_service": "ซัก-อบ {size} kg.",
    "machine.unavailable": "เครื่องไม่พร้อมใช้งาน",
    "machine.in_use": "เครื่องกำลังทำงาน",
    "machine.number": "หมายเลข",
    "machine.estimated_time": "เวลาโดยประมาณ",
    "machine.amount": "จำนวนเงิน",
    "machine.machine_number": "หมายเลขเครื่อง",
    "machine.details": "รายละเอียด",
    "machine.select_program": "เลือกโปรแกรมจากหน้าเครื่อง",
    "machine.use_machine": "ใช้บริการเครื่อง",
    "machine.wash": "ซัก",
    "machine.dry": "อบ",

    // Common additional
    "common.minutes": "นาที",
    "common.branch": "สาขา",
    "common.price": "ราคา",
    "common.total_all": "รวมทั้งหมด",

    // Payment/Summary
    "payment.pay_service": "ชำระค่าบริการ",
    "payment.service_discount": "ส่วนลดค่าบริการ",
    "payment.redeem_points": "แลกคะแนนและสะสมเพื่อรับส่วนลด",
    "payment.use_coupon": "ใช้คูปองเพื่อรับส่วนลดค่าบริการ",
    "payment.status_discount": "ส่วนลดสถานะ",
    "payment.points_discount": "ส่วนลดคะแนนสะสม",
    "payment.coupon_discount": "ส่วนลดคูปอง",
    "payment.confirm_payment": "ยืนยันการชำระเงิน",
    "payment.confirm_your_payment": "ยืนยันการชำระเงินของคุณ",
    "payment.amount_to_pay": "จำนวนเงินที่ต้องชำระ",
    "payment.pay_via_wallet": "ชำระเงินผ่านกระเป๋าเงิน",
    "payment.balance_remaining": "คงเหลือ",

    // Points/Benefits
    "points.remaining_points": "คะแนนคงเหลือ",
    "points.total_points": "คะแนนรวมสะสม",
    "points.used_points": "คะแนนที่ใช้",
    "points.special_points": "คะแนนพิเศษ",
    "points.use_points": "ใช้คะแนน",
    "points.earn_points": "ได้รับคะแนน",
    "points.expire_date": "สิ้นสุด : {date}",
    "points.special_deals": "ดีลคะแนนพิเศษสำหรับคุณ",
    "points.points_from_exchange": "คะแนนพิเศษที่ได้รับจากการแลกในครั้งนี้",
    "points.total_points_all": "ยอดรวมคะแนน ทั้งหมด",
    "points.use_accumulated_points": "ใช้คะแนนสะสม",
    "points.accumulated_points": "คะแนนสะสม",

    // Wallet/Payment
    "wallet.wallet": "กระเป๋าเงิน",
    "wallet.payment": "ชำระเงิน",
    "wallet.top_up": "เติมเงิน",
    "wallet.balance": "ยอดคงเหลือ",
    "wallet.my_coupons": "คูปองของฉัน",
    "wallet.my_coupons_short": "ของฉัน",
    "wallet.save_qr": "บันทึก QR Code",
    "wallet.payment_instruction": "บันทึก QR นี้และทำการชำระเงินผ่านแอพธนาคาร",
    "wallet.amount_to_topup": "จำนวนเงินที่ต้องการเติม (฿)",

    // Account/Profile
    "account.personal_info": "ข้อมูลส่วนตัว",
    "account.first_name": "ชื่อ",
    "account.last_name": "นามสกุล",
    "account.phone": "เบอร์โทร",
    "account.email": "อีเมล",
    "account.birthday": "วันเกิด",
    "account.day": "วัน",
    "account.month": "เดือน",
    "account.year": "ปี",
    "account.gender": "เพศ",
    "account.male": "ชาย",
    "account.female": "หญิง",
    "account.other": "ไม่ระบุ",
    "account.address": "เลขที่ / อาคาร / ตึก / หมู่บ้าน / ถนน",
    "account.province": "จังหวัด",
    "account.district": "อำเภอ / เขต",
    "account.subdistrict": "ตำบล / แขวง",
    "account.zip_code": "รหัสไปรษณีย์",

    // History
    "history.history": "ประวัติ",
    "history.topup": "เติมเงิน",
    "history.service_usage": "ใช้บริการ",
    "history.coupon_usage": "คูปอง",
    "history.points_usage": "คะแนน",
    "history.special_privileges": "สิทธิพิเศษ",

    // Gift/Rewards
    "gift.premium_exchange": "รวมคะแนนสำหรับแลกของ Premium",
    "gift.delivery_fee": "ค่าส่ง",
    "gift.total_points_summary": "รวมคะแนน",
    "gift.cannot_select_design": "ไม่สามารถเลือกแบบหรือเลือกสีได้",

    // Terms and Privacy
    "terms.service_agreement": "ข้อตกลงการใช้บริการ",
    "terms.privacy_policy": "นโยบายความเป็นส่วนตัว",
    "terms.accept_terms": "ยอมรับข้อตกลง",
    "terms.personal_data_protection": "การคุ้มครองข้อมูลส่วนบุคคล",

    // Validation messages
    "validation.required": "ข้อมูลนี้จำเป็น",
    "validation.invalid_email": "รูปแบบอีเมลไม่ถูกต้อง",
    "validation.invalid_phone": "รูปแบบเบอร์โทรไม่ถูกต้อง",
    "validation.phone_required": "กรุณากรอกเบอร์โทร",
    "validation.email_required": "กรุณากรอกอีเมล",

    // OTP
    "otp.enter_otp": "กรอก OTP ทีส่งไปยังหมายเลข",
    "otp.resend": "ส่ง OTP ใหม่",

    // Status
    "status.working": "กำลังทำงาน",
    "status.available": "ว่าง",
    "status.maintenance": "ปรับปรุง",
    "status.out_of_order": "เสีย",

    // Drop & Go
    "dropgo.queue_number": "ลำดับคิว",
    "dropgo.branch_code": "รหัสสาขา",
    "dropgo.service_usage": "การใช้บริการ",
    "dropgo.extra_dry_time": "เพิ่มเวลาอบ",
    "dropgo.detergent": "น้ำยาซักผ้า",
    "dropgo.softener": "น้ำยาปรับผ้านุ่ม",
    "dropgo.service_fee": "ค่าบริการ Drop&Go",
    "dropgo.service_discount": "ส่วนลดค่าบริการ Drop&Go",
    "dropgo.select_washer_size": "เลือกขนาดเครื่องซัก",
    "dropgo.select_dryer_size": "ขนาดเครื่องอบ",
    "dropgo.select_water_temp": "เลือกอุณหภูมิน้ำ",
    "dropgo.select_dryer_temp": "เลือกอุณหภูมิ",
    "dropgo.no_extra_time": "ไม่เพิ่มเวลา",
    "dropgo.please_select_washer": "กรุณาเลือกเครื่องซักก่อน",
    "dropgo.please_select_dryer": "กรุณาเลือกเครื่องอบก่อน",
    "dropgo.shop_has_detergent": "ทางร้านมีน้ำยาซักผ้าและน้ำยาปรับผ้านุ่มในปริมาณที่เหมาะสมกับขนาดของเครื่อง",
    "dropgo.need_extra_detergent": "ต้องการเพิ่มน้ำยา",
    "dropgo.per_pack": "ซองละ {price} บาท",
    "dropgo.use_own_detergent": "ใช้น้ำยาที่นำมาเอง",
    "dropgo.photo_basket": "ถ่ายรูปตะกร้าผ้าของคุณ",
    "dropgo.photo_note": "*ในกรณีที่นำน้ำยาซักมาเอง ให้ถ่ายรูปตะกร้าผ้าคู่กับน้ำยาซักผ้าของคุณ",
    "dropgo.notes_placeholder": "หมายเหตุถึงพนักงาน",
    "dropgo.accept_terms": "ยอมรับเงื่อนไขการใช้บริการ",
    "dropgo.cold_water": "น้ำเย็น",
    "dropgo.warm_water": "น้ำอุ่น",
    "dropgo.hot_water": "น้ำร้อน",
    "dropgo.no_washers_available": "ไม่มีเครื่องซักในสาขานี้",
    "dropgo.no_dryers_available": "ไม่มีเครื่องอบในสาขานี้",
    "payment.membership_discount": "ส่วนลดสถานะสมาชิก",
  },

  en: {
    // Common/General
    "common.points": "Points",
    "common.baht": "Baht",
    "common.next": "Next",
    "common.back": "Back",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.confirm": "Confirm",
    "common.edit": "Edit",
    "common.send": "Send",
    "common.loading": "Loading...",
    "common.error": "Error",
    "common.success": "Success",
    "common.retry": "Try Again",
    "common.error_sorry": "Sorry, an error occurred",
    "common.close": "Close",
    "common.select": "Select",
    "common.total": "Total",
    "common.quantity": "Quantity",
    "common.piece": "Piece",
    "common.all": "All",

    // Navigation/Menu
    "nav.home": "Home",
    "nav.history": "History",
    "nav.account": "Account",
    "nav.benefits": "Benefits",
    "nav.wallet": "Wallet",
    "nav.coupon": "Coupon",
    "coupon.available": "Available",
    "coupon.save": "Save Coupon",
    "coupon.used": "Used",
    "coupon.expired": "Expired",
    "nav.notification": "Notification",
    "nav.branch": "Branch",
    "nav.logout": "Logout",
    "nav.me": "Me",

    // Menu
    "menu.about": "About LAUNDRY STATION",
    "menu.terms_and_policies": "Terms and Other Policies",
    "menu.delete_account": "Delete Account",

    // Home page
    "home.remaining_balance": "Remaining Balance",
    "home.top_up": "Top Up",
    "home.wash": "Wash",
    "home.dry": "Dry",
    "home.wash_dry": "Wash & Dry",
    "home.fold": "Fold",
    "home.services": "Services",
    "home.drop_and_go": "DROP & GO",
    "home.check_nearby_branch": "Check Nearby Branch",
    "home.my_basket": "My Basket",
    "home.maid": "Staff",

    // Machine types
    "machine.washer": "Washer",
    "machine.dryer": "Dryer",
    "machine.washer_dryer": "Washer & Dryer",
    "machine.wash_service": "Wash {size} kg.",
    "machine.dry_service": "Dry {size} kg.",
    "machine.wash_dry_service": "Wash & Dry {size} kg.",
    "machine.unavailable": "Machine Unavailable",
    "machine.in_use": "Machine In Use",
    "machine.number": "Number",
    "machine.estimated_time": "Estimated Time",
    "machine.amount": "Amount",
    "machine.machine_number": "Machine Number",
    "machine.details": "Details",
    "machine.select_program": "Select program from machine",
    "machine.use_machine": "Use Machine",
    "machine.wash": "Wash",
    "machine.dry": "Dry",

    // Common additional
    "common.minutes": "Minutes",
    "common.branch": "Branch",
    "common.price": "Price",
    "common.total_all": "Total",

    // Payment/Summary
    "payment.pay_service": "Pay for Service",
    "payment.service_discount": "Service Discount",
    "payment.redeem_points": "Redeem points for discount",
    "payment.use_coupon": "Use coupon for service discount",
    "payment.status_discount": "Status Discount",
    "payment.points_discount": "Points Discount",
    "payment.coupon_discount": "Coupon Discount",
    "payment.confirm_payment": "Confirm Payment",
    "payment.confirm_your_payment": "Confirm Your Payment",
    "payment.amount_to_pay": "Amount to Pay",
    "payment.pay_via_wallet": "Pay via Wallet",
    "payment.balance_remaining": "Balance",

    // Points/Benefits
    "points.remaining_points": "Remaining Points",
    "points.total_points": "Total Accumulated Points",
    "points.used_points": "Used Points",
    "points.special_points": "Special Points",
    "points.use_points": "Use Points",
    "points.earn_points": "Earn Points",
    "points.expire_date": "Expires: {date}",
    "points.special_deals": "Special Point Deals for You",
    "points.points_from_exchange": "Special points earned from this exchange",
    "points.total_points_all": "Total Points",
    "points.use_accumulated_points": "Use Accumulated Points",
    "points.accumulated_points": "Points",

    // Wallet/Payment
    "wallet.wallet": "Wallet",
    "wallet.payment": "Payment",
    "wallet.top_up": "Top Up",
    "wallet.balance": "Balance",
    "wallet.my_coupons": "My Coupons",
    "wallet.my_coupons_short": "Mine",
    "wallet.save_qr": "Save QR Code",
    "wallet.payment_instruction": "Save this QR and pay via banking app",
    "wallet.amount_to_topup": "Amount to Top Up (฿)",

    // Account/Profile
    "account.personal_info": "Personal Information",
    "account.first_name": "First Name",
    "account.last_name": "Last Name",
    "account.phone": "Phone",
    "account.email": "Email",
    "account.birthday": "Birthday",
    "account.day": "Day",
    "account.month": "Month",
    "account.year": "Year",
    "account.gender": "Gender",
    "account.male": "Male",
    "account.female": "Female",
    "account.other": "Other",
    "account.address": "Address / Building / Road",
    "account.province": "Province",
    "account.district": "District",
    "account.subdistrict": "Subdistrict",
    "account.zip_code": "Zip Code",

    // History
    "history.history": "History",
    "history.topup": "Top Up",
    "history.service_usage": "Service Usage",
    "history.coupon_usage": "Coupon",
    "history.points_usage": "Points",
    "history.special_privileges": "Special Privileges",

    // Gift/Rewards
    "gift.premium_exchange": "Points for Premium Exchange",
    "gift.delivery_fee": "Delivery Fee",
    "gift.total_points_summary": "Total Points",
    "gift.cannot_select_design": "Cannot select design or color",

    // Terms and Privacy
    "terms.service_agreement": "Service Agreement",
    "terms.privacy_policy": "Privacy Policy",
    "terms.accept_terms": "Accept Terms",
    "terms.personal_data_protection": "Personal Data Protection",

    // Validation messages
    "validation.required": "This field is required",
    "validation.invalid_email": "Invalid email format",
    "validation.invalid_phone": "Invalid phone format",
    "validation.phone_required": "Phone number is required",
    "validation.email_required": "Email is required",

    // OTP
    "otp.enter_otp": "Enter OTP sent to number",
    "otp.resend": "Resend OTP",

    // Status
    "status.working": "Working",
    "status.available": "Available",
    "status.maintenance": "Maintenance",
    "status.out_of_order": "Out of Order",

    // Drop & Go
    "dropgo.queue_number": "Queue Number",
    "dropgo.branch_code": "Branch Code",
    "dropgo.service_usage": "Service Usage",
    "dropgo.extra_dry_time": "Extra Dry Time",
    "dropgo.detergent": "Detergent",
    "dropgo.softener": "Fabric Softener",
    "dropgo.service_fee": "Drop&Go Service Fee",
    "dropgo.service_discount": "Drop&Go Service Discount",
    "dropgo.select_washer_size": "Select Washer Size",
    "dropgo.select_dryer_size": "Dryer Size",
    "dropgo.select_water_temp": "Select Water Temperature",
    "dropgo.select_dryer_temp": "Select Temperature",
    "dropgo.no_extra_time": "No extra time",
    "dropgo.please_select_washer": "Please select a washer first",
    "dropgo.please_select_dryer": "Please select a dryer first",
    "dropgo.shop_has_detergent": "The shop has detergent and softener in suitable amounts for the machine size",
    "dropgo.need_extra_detergent": "Need Extra Detergent",
    "dropgo.per_pack": "{price} Baht per pack",
    "dropgo.use_own_detergent": "Use Own Detergent",
    "dropgo.photo_basket": "Take Photo of Your Laundry Basket",
    "dropgo.photo_note": "*If bringing your own detergent, take a photo of your laundry basket with your detergent",
    "dropgo.notes_placeholder": "Notes for Staff",
    "dropgo.accept_terms": "Accept Terms of Service",
    "dropgo.cold_water": "Cold Water",
    "dropgo.warm_water": "Warm Water",
    "dropgo.hot_water": "Hot Water",
    "dropgo.no_washers_available": "No washers available at this branch",
    "dropgo.no_dryers_available": "No dryers available at this branch",
    "payment.membership_discount": "Membership Discount",
  },

  // Burmese, for the staff who read it. The dashboard words live in
  // locales/maid.js; these are the ones the rest of the app says.
  my: {
    // Common/General
    "common.points": "အမှတ်",
    "common.baht": "ဘတ်",
    "common.next": "ရှေ့သို့",
    "common.back": "နောက်သို့",
    "common.save": "သိမ်းရန်",
    "common.cancel": "ပယ်ဖျက်",
    "common.confirm": "အတည်ပြု",
    "common.edit": "ပြင်ဆင်",
    "common.send": "ပို့ရန်",
    "common.loading": "ဖွင့်နေသည်...",
    "common.error": "အမှားဖြစ်ပွားခဲ့သည်",
    "common.success": "အောင်မြင်ပါသည်",
    "common.retry": "ထပ်မံကြိုးစားရန်",
    "common.error_sorry": "တောင်းပန်ပါသည်၊ အမှားဖြစ်ပွားခဲ့သည်",
    "common.close": "ပိတ်ရန်",
    "common.select": "ရွေးရန်",
    "common.total": "စုစုပေါင်း",
    "common.quantity": "အရေအတွက်",
    "common.piece": "ခု",
    "common.all": "အားလုံး",
    "common.minutes": "မိနစ်",
    "common.branch": "ဆိုင်ခွဲ",
    "common.price": "ဈေးနှုန်း",
    "common.total_all": "စုစုပေါင်းအားလုံး",

    // Navigation
    "nav.home": "ပင်မ",
    "nav.history": "မှတ်တမ်း",
    "nav.account": "အကောင့်",
    "nav.benefits": "အထူးအခွင့်အရေး",
    "nav.wallet": "ပိုက်ဆံအိတ်",
    "nav.coupon": "ကူပွန်",
    "nav.notification": "အသိပေးချက်",
    "nav.branch": "ဆိုင်ခွဲ",
    "nav.logout": "ထွက်ရန်",
    "nav.me": "ကျွန်ုပ်",

    // Coupons
    "coupon.available": "သုံးနိုင်သောကူပွန်",
    "coupon.save": "ကူပွန်သိမ်းရန်",
    "coupon.used": "သုံးပြီးကူပွန်",
    "coupon.expired": "သက်တမ်းကုန်ကူပွန်",

    // Menu
    "menu.about": "LAUNDRY STATION အကြောင်း",
    "menu.terms_and_policies": "စည်းကမ်းချက်နှင့် မူဝါဒများ",
    "menu.delete_account": "အကောင့်ဖျက်ရန်",

    // Home
    "home.remaining_balance": "ကျန်ငွေ",
    "home.top_up": "ငွေဖြည့်",
    "home.wash": "လျှော်",
    "home.dry": "အခြောက်ခံ",
    "home.wash_dry": "လျှော်-အခြောက်ခံ",
    "home.fold": "ခေါက်",
    "home.services": "ဝန်ဆောင်မှုများ",
    "home.drop_and_go": "DROP & GO",
    "home.check_nearby_branch": "အနီးအနားဆိုင်ခွဲရှာရန်",
    "home.my_basket": "ကျွန်ုပ်၏တောင်း",
    "home.maid": "ဝန်ထမ်း",

    // Machines
    "machine.washer": "အဝတ်လျှော်စက်",
    "machine.dryer": "အခြောက်ခံစက်",
    "machine.washer_dryer": "လျှော်-အခြောက်ခံစက်",
    "machine.wash_service": "လျှော် {size} kg.",
    "machine.dry_service": "အခြောက်ခံ {size} kg.",
    "machine.wash_dry_service": "လျှော်-အခြောက်ခံ {size} kg.",
    "machine.unavailable": "စက်အသုံးပြု၍မရပါ",
    "machine.in_use": "စက်အလုပ်လုပ်နေသည်",
    "machine.number": "နံပါတ်",
    "machine.estimated_time": "ခန့်မှန်းအချိန်",
    "machine.amount": "ငွေပမာဏ",
    "machine.machine_number": "စက်နံပါတ်",
    "machine.details": "အသေးစိတ်",
    "machine.select_program": "စက်ပေါ်မှ ပရိုဂရမ်ကိုရွေးပါ",
    "machine.use_machine": "စက်ကိုအသုံးပြုရန်",
    "machine.wash": "လျှော်",
    "machine.dry": "အခြောက်ခံ",

    // Payment
    "payment.pay_service": "ဝန်ဆောင်ခပေးချေရန်",
    "payment.service_discount": "ဝန်ဆောင်ခလျှော့ဈေး",
    "payment.redeem_points": "အမှတ်များလဲလှယ်ပြီး လျှော့ဈေးရယူပါ",
    "payment.use_coupon": "ကူပွန်သုံးပြီး ဝန်ဆောင်ခလျှော့ဈေးရယူပါ",
    "payment.status_discount": "အဆင့်လျှော့ဈေး",
    "payment.points_discount": "အမှတ်လျှော့ဈေး",
    "payment.coupon_discount": "ကူပွန်လျှော့ဈေး",
    "payment.confirm_payment": "ငွေပေးချေမှုအတည်ပြုရန်",
    "payment.confirm_your_payment": "သင့်ငွေပေးချေမှုကို အတည်ပြုပါ",
    "payment.amount_to_pay": "ပေးချေရမည့်ငွေပမာဏ",
    "payment.pay_via_wallet": "ပိုက်ဆံအိတ်ဖြင့်ပေးချေရန်",
    "payment.balance_remaining": "ကျန်ငွေ",
    "payment.membership_discount": "အသင်းဝင်အဆင့်လျှော့ဈေး",

    // Points
    "points.remaining_points": "ကျန်ရှိအမှတ်",
    "points.total_points": "စုစုပေါင်းစုဆောင်းအမှတ်",
    "points.used_points": "သုံးပြီးအမှတ်",
    "points.special_points": "အထူးအမှတ်",
    "points.use_points": "အမှတ်သုံးရန်",
    "points.earn_points": "ရရှိသောအမှတ်",
    "points.expire_date": "ကုန်ဆုံးရက် - {date}",
    "points.special_deals": "သင့်အတွက် အထူးအမှတ်ကမ်းလှမ်းချက်",
    "points.points_from_exchange": "ဤအကြိမ်လဲလှယ်မှုမှ ရရှိသောအထူးအမှတ်",
    "points.total_points_all": "စုစုပေါင်းအမှတ်",
    "points.use_accumulated_points": "စုဆောင်းအမှတ်သုံးရန်",
    "points.accumulated_points": "စုဆောင်းအမှတ်",

    // Wallet
    "wallet.wallet": "ပိုက်ဆံအိတ်",
    "wallet.payment": "ငွေပေးချေရန်",
    "wallet.top_up": "ငွေဖြည့်",
    "wallet.balance": "ကျန်ငွေ",
    "wallet.my_coupons": "ကျွန်ုပ်၏ကူပွန်",
    "wallet.my_coupons_short": "ကျွန်ုပ်၏",
    "wallet.save_qr": "QR ကုဒ်သိမ်းရန်",
    "wallet.payment_instruction":
      "ဤ QR ကိုသိမ်းပြီး ဘဏ်အက်ပ်မှတစ်ဆင့် ငွေပေးချေပါ",
    "wallet.amount_to_topup": "ဖြည့်လိုသောငွေပမာဏ (฿)",

    // Account
    "account.personal_info": "ကိုယ်ရေးအချက်အလက်",
    "account.first_name": "အမည်",
    "account.last_name": "မျိုးနွယ်အမည်",
    "account.phone": "ဖုန်းနံပါတ်",
    "account.email": "အီးမေးလ်",
    "account.birthday": "မွေးနေ့",
    "account.day": "ရက်",
    "account.month": "လ",
    "account.year": "နှစ်",
    "account.gender": "ကျား/မ",
    "account.male": "ကျား",
    "account.female": "မ",
    "account.other": "မဖော်ပြလို",
    "account.address": "အမှတ် / အဆောက်အအုံ / ရပ်ကွက် / လမ်း",
    "account.province": "ပြည်နယ်/တိုင်း",
    "account.district": "မြို့နယ်",
    "account.subdistrict": "ရပ်ကွက်/ကျေးရွာ",
    "account.zip_code": "စာပို့သင်္ကေတ",

    // History
    "history.history": "မှတ်တမ်း",
    "history.topup": "ငွေဖြည့်",
    "history.service_usage": "ဝန်ဆောင်မှုသုံးစွဲမှု",
    "history.coupon_usage": "ကူပွန်",
    "history.points_usage": "အမှတ်",
    "history.special_privileges": "အထူးအခွင့်အရေး",

    // Gifts
    "gift.premium_exchange": "Premium ပစ္စည်းလဲရန် အမှတ်စုစုပေါင်း",
    "gift.delivery_fee": "ပို့ဆောင်ခ",
    "gift.total_points_summary": "အမှတ်စုစုပေါင်း",
    "gift.cannot_select_design": "ဒီဇိုင်း သို့မဟုတ် အရောင်ရွေး၍မရပါ",

    // Terms
    "terms.service_agreement": "ဝန်ဆောင်မှုသုံးစွဲမှုသဘောတူညီချက်",
    "terms.privacy_policy": "ကိုယ်ရေးအချက်အလက်မူဝါဒ",
    "terms.accept_terms": "သဘောတူညီချက်ကိုလက်ခံသည်",
    "terms.personal_data_protection": "ကိုယ်ရေးအချက်အလက်ကာကွယ်မှု",

    // Validation
    "validation.required": "ဤအချက်အလက်လိုအပ်သည်",
    "validation.invalid_email": "အီးမေးလ်ပုံစံမမှန်ကန်ပါ",
    "validation.invalid_phone": "ဖုန်းနံပါတ်ပုံစံမမှန်ကန်ပါ",
    "validation.phone_required": "ဖုန်းနံပါတ်ဖြည့်ပါ",
    "validation.email_required": "အီးမေးလ်ဖြည့်ပါ",

    // OTP
    "otp.enter_otp": "ဤနံပါတ်သို့ပို့သော OTP ကိုဖြည့်ပါ",
    "otp.resend": "OTP ပြန်ပို့ရန်",

    // Status
    "status.working": "အလုပ်လုပ်နေသည်",
    "status.available": "အားလပ်",
    "status.maintenance": "ပြုပြင်ဆဲ",
    "status.out_of_order": "ပျက်နေသည်",

    // Drop & Go
    "dropgo.queue_number": "တန်းစီနံပါတ်",
    "dropgo.branch_code": "ဆိုင်ခွဲကုဒ်",
    "dropgo.service_usage": "အသုံးပြုသောဝန်ဆောင်မှု",
    "dropgo.extra_dry_time": "အခြောက်ခံအချိန်ထပ်ထည့်",
    "dropgo.detergent": "ဆပ်ပြာရည်",
    "dropgo.softener": "အဝတ်ပျော့ဆေး",
    "dropgo.service_fee": "Drop&Go ဝန်ဆောင်ခ",
    "dropgo.service_discount": "Drop&Go ဝန်ဆောင်ခလျှော့ဈေး",
    "dropgo.select_washer_size": "အဝတ်လျှော်စက်အရွယ်အစားရွေးပါ",
    "dropgo.select_dryer_size": "အခြောက်ခံစက်အရွယ်အစား",
    "dropgo.select_water_temp": "ရေအပူချိန်ရွေးပါ",
    "dropgo.select_dryer_temp": "အပူချိန်ရွေးပါ",
    "dropgo.no_extra_time": "အချိန်မထပ်ထည့်ပါ",
    "dropgo.please_select_washer": "အဝတ်လျှော်စက်ကို ဦးစွာရွေးပါ",
    "dropgo.please_select_dryer": "အခြောက်ခံစက်ကို ဦးစွာရွေးပါ",
    "dropgo.shop_has_detergent":
      "ဆိုင်တွင် စက်အရွယ်အစားနှင့်လိုက်ဖက်သော ဆပ်ပြာရည်နှင့် အဝတ်ပျော့ဆေးရှိပါသည်",
    "dropgo.need_extra_detergent": "ဆပ်ပြာရည်ထပ်ထည့်လိုသည်",
    "dropgo.per_pack": "တစ်ထုပ်လျှင် {price} ဘတ်",
    "dropgo.use_own_detergent": "ကိုယ်ယူလာသောဆပ်ပြာရည်သုံးမည်",
    "dropgo.photo_basket": "သင့်အဝတ်တောင်းကို ဓာတ်ပုံရိုက်ပါ",
    "dropgo.photo_note":
      "*ဆပ်ပြာရည်ကိုယ်တိုင်ယူလာပါက အဝတ်တောင်းနှင့်အတူ ဓာတ်ပုံရိုက်ပါ",
    "dropgo.notes_placeholder": "ဝန်ထမ်းသို့မှတ်ချက်",
    "dropgo.accept_terms": "ဝန်ဆောင်မှုစည်းကမ်းချက်များကိုလက်ခံသည်",
    "dropgo.cold_water": "ရေအေး",
    "dropgo.warm_water": "ရေနွေးလေး",
    "dropgo.hot_water": "ရေပူ",
    "dropgo.no_washers_available": "ဤဆိုင်ခွဲတွင် အဝတ်လျှော်စက်မရှိပါ",
    "dropgo.no_dryers_available": "ဤဆိုင်ခွဲတွင် အခြောက်ခံစက်မရှိပါ",
  },
};

// The maid's screens and the Drop & Go screens keep their own words in one
// file each, per language; fold them into the dictionary the app reads.
[dropgoTranslations, maidTranslations].forEach((extra) => {
  Object.keys(extra).forEach((lang) => {
    translations[lang] = {
      ...(translations[lang] || {}),
      ...extra[lang],
    };
  });
});

/**
 * React hook for using translations
 * @returns {Object} Object containing translation function and current language
 */
export const useTranslation = () => {
  const [currentLang, setCurrentLang] = React.useState(getCurrentLanguage());

  React.useEffect(() => {
    const handleLanguageChange = (event) => {
      setCurrentLang(event.detail);
    };

    window.addEventListener("languageChanged", handleLanguageChange);
    return () =>
      window.removeEventListener("languageChanged", handleLanguageChange);
  }, []);

  return {
    t,
    currentLang,
    setLanguage: setCurrentLanguage,
    supportedLanguages: getSupportedLanguages(),
  };
};

export default {
  t,
  getCurrentLanguage,
  setCurrentLanguage,
  getSupportedLanguages,
  useTranslation,
};
